export const LEGACY_SHIPMENT_DOC_TYPE_CODES = {
  remito_carga: "RC",
  remito_descarga: "RE",
} as const

export type LegacyShipmentDocType = keyof typeof LEGACY_SHIPMENT_DOC_TYPE_CODES

export const DEFAULT_SHIPMENT_DOC_TYPE_CODE = LEGACY_SHIPMENT_DOC_TYPE_CODES.remito_descarga

/** Codes resolved by code (mobile uploads, legacy values): they cannot be deleted or renamed. */
export const PROTECTED_SHIPMENT_DOC_TYPE_CODES: readonly string[] = Object.values(LEGACY_SHIPMENT_DOC_TYPE_CODES)

export function legacyShipmentDocTypeToCode(value: string | null | undefined): string {
  if (value === "remito_carga" || value === "remito_descarga") return LEGACY_SHIPMENT_DOC_TYPE_CODES[value]
  return DEFAULT_SHIPMENT_DOC_TYPE_CODE
}

export function codeToLegacyShipmentDocType(code: string): LegacyShipmentDocType | null {
  const entry = Object.entries(LEGACY_SHIPMENT_DOC_TYPE_CODES).find(([, legacyCode]) => legacyCode === code)
  return entry ? (entry[0] as LegacyShipmentDocType) : null
}

export function isProtectedShipmentDocTypeCode(code: string): boolean {
  return PROTECTED_SHIPMENT_DOC_TYPE_CODES.includes(code)
}

export type ShipmentDocTypeLookup =
  | { by: "id"; id: string }
  | { by: "code"; code: string }

/**
 * An explicit catalog id wins; otherwise the legacy enum (or its absence, as sent by the
 * driver app) is resolved by code so mobile uploads always land on RE.
 */
export function shipmentDocTypeLookup(
  docTypeId: string | null | undefined,
  legacyDocType: string | null | undefined,
): ShipmentDocTypeLookup {
  return docTypeId
    ? { by: "id", id: docTypeId }
    : { by: "code", code: legacyShipmentDocTypeToCode(legacyDocType) }
}

/** Whether a doc PATCH must re-resolve the type. Re-sending the current id is a no-op. */
export function isShipmentDocTypeChange(
  patch: { docType?: string | null | undefined; docTypeId?: string | null | undefined },
  currentDocTypeId: string,
): boolean {
  if (patch.docType === undefined && patch.docTypeId === undefined) return false
  return patch.docTypeId !== currentDocTypeId
}

/**
 * Letter to store on a shipment doc. An explicit value (including null) always wins;
 * otherwise it falls back to the type's control. `undefined` means "leave unchanged" (PATCH).
 */
export function resolveShipmentDocLetter(
  requested: string | null | undefined,
  typeControl: string | null | undefined,
): string | null | undefined {
  return requested !== undefined ? requested : typeControl
}
